import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import GoalButton from "layouts/goals/components/GoalButton";
import FeatureTree from "layouts/organizations/components/FeatureTree";
import { hasAllFeatures } from "layouts/organizations/organizations.constants";
import { ROLES_MESSAGES } from "../roles.constants";

// Adds a role or edits one. Only the features the organization has can be
// picked; the rest are greyed out. Built-in roles keep their name.
function RoleFormModal({ open, onClose, onSave, isSubmitting, role, organizationFeatures }) {
  const isEdit = !!role;
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [localError, setLocalError] = useState(null);

  const available = useMemo(
    () => (hasAllFeatures(organizationFeatures) ? null : organizationFeatures),
    [organizationFeatures]
  );

  useEffect(() => {
    setName(role?.name || "");
    // Anything the organization no longer has is dropped when the role is saved.
    setSelected((role?.permissions || []).filter((key) => !available || available.includes(key)));
    setLocalError(null);
  }, [role, open, available]);

  const handleSave = async () => {
    if (!name.trim()) return setLocalError(ROLES_MESSAGES.nameRequired);
    if (selected.length === 0) return setLocalError(ROLES_MESSAGES.featuresRequired);
    setLocalError(null);
    await onSave({ name, permissions: selected });
  };

  return (
    <Dialog open={open} onClose={() => !isSubmitting && onClose()} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontSize: 18 }}>{isEdit ? "Edit role" : "Add role"}</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {localError && (
          <SuiTypography variant="caption" color="error" display="block" mb={1}>
            {localError}
          </SuiTypography>
        )}
        <SuiTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
          Role name
        </SuiTypography>
        <SuiInput
          autoComplete="off"
          placeholder="e.g. Office Manager"
          disabled={isEdit && role.is_system}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <SuiTypography variant="caption" color="text" fontWeight="medium" display="block" mt={2}>
          Features
        </SuiTypography>
        <SuiTypography variant="caption" color="secondary" display="block" mb={0.5}>
          {available
            ? "Greyed-out features aren't available to your organization."
            : "Everything your organization has can be given to this role."}
        </SuiTypography>
        <Divider sx={{ mb: 1 }} />
        <FeatureTree selected={selected} onChange={setSelected} available={available} />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GoalButton variant="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </GoalButton>
        <GoalButton variant="primary" icon="save" loading={isSubmitting} onClick={handleSave}>
          {isEdit ? "Save changes" : "Add role"}
        </GoalButton>
      </DialogActions>
    </Dialog>
  );
}

export default RoleFormModal;
